import React, { useEffect, useState } from "react";
import styles from "./Schedule.module.scss";
import PageTitle from "../../../components/pageTitle/PageTitle";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
} from "date-fns";
import { ReactComponent as ArrowLeftIcon } from "./ArrowLeft.svg";
import { ReactComponent as ArrowRightIcon } from "./ArrowRight.svg";
import { ReactComponent as XIcon } from "./X.svg";
import axiosClient from "../../../api/axiosClient";
import { useToasts } from "../../../contexts/ToastContext";
import { useNavigate } from "react-router-dom";
import ProfilePicture from "../../../components/profilePicture/ProfilePicture";
import Button from "../../../components/button/Button";
import StudentCard from "./components/StudentCard";
import FilterGroup from "../../../components/filterGroup/FilterGroup";

const VISIBLE_COUNT = 2;

const Schedule = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [scheduleData, setScheduleData] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filterData, setFilterData] = useState({ students: [], clinicalSites: [], specialities: [], years: [] });
  const [activeFilters, setActiveFilters] = useState({ student: [], clinicalSite: [], specialty: [], year: [] });

  // Single-item popup
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0, isLastRow: false });

  // Day ("see more") popup
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayPopupPosition, setDayPopupPosition] = useState({ top: 0, left: 0 });

  const calendarWrapperRef = React.useRef(null);
  const popupRef = React.useRef(null);
  const dayPopupRef = React.useRef(null);
  const { showToast } = useToasts();

  const getMonthDays = (date) => {
    const start = startOfWeek(startOfMonth(date), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(date), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  };

  const monthDays = getMonthDays(currentDate);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
        const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

        const response = await axiosClient.get(
          `/program-schedules/instructor/allocated-students`,
          {
            params: {
              startDate: format(gridStart, "yyyy-MM-dd"),
              endDate: format(gridEnd, "yyyy-MM-dd"),
              ...(activeFilters.student.length && { studentIds: activeFilters.student }),
              ...(activeFilters.clinicalSite.length && { clinicalSiteIds: activeFilters.clinicalSite }),
              ...(activeFilters.specialty.length && { specialityIds: activeFilters.specialty }),
              ...(activeFilters.year.length && { yearIds: activeFilters.year }),
            },
          }
        );

        if (response.data) {
          setScheduleData(response.data.data || []);
          setFilterData({
            students: response.data.students || [],
            clinicalSites: response.data.clinicalSites || [],
            specialities: response.data.specialities || [],
            years: response.data.years || [],
          });
        } else {
          setScheduleData([]);
        }
      } catch (error) {
        showToast(error.message, "error");
        setScheduleData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentDate, activeFilters, showToast]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        !event.target.closest(`.${styles.studentItem}`)
      ) {
        setSelectedStudent(null);
      }
      if (
        dayPopupRef.current &&
        !dayPopupRef.current.contains(event.target) &&
        !event.target.closest(`.${styles.moreItems}`)
      ) {
        setSelectedDay(null);
      }
    };

    if (selectedStudent || selectedDay) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [selectedStudent, selectedDay]);

  const getStudentsForDate = (day) => {
    const dateStr = format(day, "yyyy-MM-dd");
    const entry = scheduleData.find((d) => d.date === dateStr);
    return entry?.students || [];
  };

  const calcPopupPosition = (e, POPUP_WIDTH = 380) => {
    if (!calendarWrapperRef.current) return null;
    const itemRect = e.currentTarget.getBoundingClientRect();
    const containerRect = calendarWrapperRef.current.getBoundingClientRect();
    const SPACING = 10;

    const spaceOnRight = containerRect.right - itemRect.right;
    const itemRightInContainer = itemRect.right - containerRect.left;
    const itemTopInContainer = itemRect.top - containerRect.top;
    const itemBottomInContainer = itemRect.bottom - containerRect.top;
    const containerHeight = containerRect.bottom - containerRect.top;

    const leftPosition =
      spaceOnRight < POPUP_WIDTH + SPACING
        ? itemRect.left - containerRect.left - POPUP_WIDTH - SPACING
        : itemRightInContainer + SPACING;

    return { leftPosition, itemTopInContainer, itemBottomInContainer, containerHeight };
  };

  const handleStudentClick = (e, student, day) => {
    e.stopPropagation();
    const pos = calcPopupPosition(e, 380);
    if (!pos) return;

    const calendarRows = Math.ceil(monthDays.length / 7);
    const dayIndex = monthDays.findIndex((d) => d.getTime() === day.getTime());
    const rowIndex = Math.floor(dayIndex / 7);
    const isLastTwoRows = rowIndex >= calendarRows - 2;

    setPopupPosition({
      left: Math.max(0, pos.leftPosition),
      isLastRow: isLastTwoRows,
      ...(isLastTwoRows
        ? { bottom: pos.containerHeight - pos.itemBottomInContainer, top: undefined }
        : { top: pos.itemTopInContainer, bottom: undefined }),
    });
    setSelectedStudent(student);
    setSelectedDay(null);
  };

  const handleMoreClick = (e, day) => {
    e.stopPropagation();
    const pos = calcPopupPosition(e, 440);
    if (!pos) return;

    const calendarRows = Math.ceil(monthDays.length / 7);
    const dayIndex = monthDays.findIndex((d) => d.getTime() === day.getTime());
    const rowIndex = Math.floor(dayIndex / 7);
    const isLastThreeRows = rowIndex >= calendarRows - 3;

    setDayPopupPosition({
      left: Math.max(0, pos.leftPosition),
      ...(isLastThreeRows
        ? { bottom: pos.containerHeight - pos.itemBottomInContainer, top: undefined }
        : { top: pos.itemTopInContainer, bottom: undefined }),
    });
    setSelectedDay(day);
    setSelectedStudent(null);
  };

const isCurrentMonth = (day) => day.getMonth() === currentDate.getMonth();
  const monthYear = format(currentDate, "MMMM yyyy");
  const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="Schedule" />
        <Button
          label="My Availability"
          variant="primary-light"
          size="small"
          onClick={() => navigate('/availability')}
        />
      </div>
      <div className={styles.container}>
        <div className={styles.calendarWrapper} ref={calendarWrapperRef}>
          <div className={styles.calendarTopRow}>
            <FilterGroup
              categories={[
                { id: 'student', label: 'Student', data: filterData.students, getLabel: (item) => `${item.firstName} ${item.lastName}`, type: 'user' },
                { id: 'clinicalSite', label: 'Clinical Site', data: filterData.clinicalSites, getLabel: (item) => item.name },
                { id: 'specialty', label: 'Specialty', data: filterData.specialities, getLabel: (item) => item.name },
                { id: 'year', label: 'Year', data: filterData.years, getLabel: (item) => item.year },
              ]}
              filters={activeFilters}
              onChange={setActiveFilters}
            />
            <div className={styles.calendarHeader}>
            <button
              className={styles.navButton}
              onClick={() => setCurrentDate(subMonths(currentDate, 1))}
              aria-label="Previous month"
            >
              <ArrowLeftIcon />
            </button>
            <div className={styles.monthYear}>
              <h2>{monthYear}</h2>
            </div>
            <button
              className={styles.navButton}
              onClick={() => setCurrentDate(addMonths(currentDate, 1))}
              aria-label="Next month"
            >
              <ArrowRightIcon />
            </button>
            </div>
          </div>

          <div className={styles.calendarGrid}>
            <div className={styles.weekHeader}>
              {dayNames.map((day) => (
                <div key={day} className={styles.dayHeader}>{day}</div>
              ))}
            </div>

            <div className={styles.monthDays}>
              {monthDays.map((day, index) => {
                const students = getStudentsForDate(day);
                const visibleStudents = students.slice(0, VISIBLE_COUNT);
                const moreCount = students.length - VISIBLE_COUNT;
                const isOtherMonth = !isCurrentMonth(day);

                return (
                  <div
                    key={index}
                    className={`${styles.dayCell} ${isOtherMonth ? styles.otherMonth : ""}`}
                  >
                    <div className={styles.dayTop}>
                      {students.length > 0 && (
                        <div className={styles.appointmentBadge}>
                          <span className={styles.dot} />
                          {students.length} Appointment{students.length !== 1 ? "s" : ""}
                        </div>
                      )}
                      <div className={styles.dayNumber}>{format(day, "d")}</div>
                    </div>
                    {students.length > 0 && (
                      <div className={styles.studentsContainer}>
                        {visibleStudents.map((student, idx) => (
                          <div
                            key={idx}
                            className={styles.studentItem}
                            onClick={(e) => handleStudentClick(e, student, day)}
                          >
                            <ProfilePicture
                              firstName={student.firstName}
                              lastName={student.lastName}
                              profilePicture={student.profilePicture}
                              size="xs"
                            />
                            <span>{`${student.firstName} ${student.lastName}`}</span>
                            {student.status && student.status !== 'Active' && (
                              <span className={`${styles.statusTag} ${styles[student.status?.toLowerCase()]}`}>
                                {student.status}
                              </span>
                            )}
                          </div>
                        ))}
                        {moreCount > 0 && (
                          <div
                            className={styles.moreItems}
                            onClick={(e) => handleMoreClick(e, day)}
                          >
                            +{moreCount} More
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Single student popup */}
          {selectedStudent && (
            <div
              ref={popupRef}
              className={styles.popup}
              style={{
                ...(popupPosition.top !== undefined && { top: `${popupPosition.top}px` }),
                ...(popupPosition.bottom !== undefined && { bottom: `${popupPosition.bottom}px` }),
                left: `${popupPosition.left}px`,
              }}
            >
              <div className={styles.close}>
                <XIcon onClick={() => setSelectedStudent(null)} />
              </div>
              <div className={styles.popupContent}>
                <StudentCard student={selectedStudent} />
              </div>
            </div>
          )}

          {/* Day "see more" popup */}
          {selectedDay && (
            <div
              ref={dayPopupRef}
              className={styles.popup}
              style={{
                ...(dayPopupPosition.top !== undefined && { top: `${dayPopupPosition.top}px` }),
                ...(dayPopupPosition.bottom !== undefined && { bottom: `${dayPopupPosition.bottom}px` }),
                left: `${dayPopupPosition.left}px`,
                minWidth: 400,
                maxWidth: 440,
                overflowY: 'auto',
                maxHeight: 500,
              }}
            >
              <div className={styles.close}>
                <XIcon onClick={() => setSelectedDay(null)} />
              </div>
              <div className={`${styles.popupContent} ${styles.dayPopupContent}`}>
                <div className={styles.dayPopupTitle}>
                  {format(selectedDay, "d MMMM yyyy")}
                </div>
                {getStudentsForDate(selectedDay).map((student, idx) => (
                  <div key={idx} className={styles.dayPopupItem}>
                    <StudentCard student={student} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

    </>
  );
};

export default Schedule;
